/**
 * Postal code validation regex patterns
 * Supporting multiple countries and formats
 */

// Simplified postal code patterns (current default)
export const POSTAL_CODE_SIMPLE_REGEX = /^[A-Za-z0-9\s\-]+$/; // Alphanumeric, spaces, and hyphens
export const POSTAL_CODE_SIMPLE_MIN_LENGTH = 3;
export const POSTAL_CODE_SIMPLE_MAX_LENGTH = 10;

// Strict postal code patterns (versioned for future use)
export const POSTAL_CODE_PATTERNS_STRICT = {
    IT: /^\d{5}$/, // Italy: 5 digits
    US: /^\d{5}$|^\d{5}-\d{4}$/, // US: 5 digits or 5+4 format
    GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, // UK: complex format
    FR: /^\d{5}$/, // France: 5 digits
    DE: /^\d{5}$/, // Germany: 5 digits
    CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i, // Canada: A1A 1A1 format
    AU: /^\d{4}$/, // Australia: 4 digits
    JP: /^\d{3}-?\d{4}$/, // Japan: 123-4567 format
    NL: /^\d{4}\s?[A-Z]{2}$/i, // Netherlands: 1234 AB format
    SG: /^\d{6}$/, // Singapore: 6 digits
    HK: /^\d{6}$/ // Hong Kong: 6 digits (adding for completeness)
};

// Legacy export (for backward compatibility - now points to strict validation)
export const POSTAL_CODE_PATTERNS = POSTAL_CODE_PATTERNS_STRICT;

// General postal code constraints
export const POSTAL_CODE_MIN_LENGTH = POSTAL_CODE_SIMPLE_MIN_LENGTH;
export const POSTAL_CODE_MAX_LENGTH = POSTAL_CODE_SIMPLE_MAX_LENGTH;

/**
 * Simplified postal code validation (current default)
 * Accepts alphanumeric characters, spaces, and hyphens, length 3-10 characters
 */
export function validatePostalCode(
    postalCode: string,
    countryCode?: string
): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];
    const trimmedCode = postalCode.trim();

    if (!trimmedCode) {
        errors.push('Postal code is required');
        return { isValid: false, errors };
    }

    // Basic length check
    if (trimmedCode.length < POSTAL_CODE_SIMPLE_MIN_LENGTH) {
        errors.push(`Postal code must be at least ${POSTAL_CODE_SIMPLE_MIN_LENGTH} characters`);
    }

    if (trimmedCode.length > POSTAL_CODE_SIMPLE_MAX_LENGTH) {
        errors.push(`Postal code cannot exceed ${POSTAL_CODE_SIMPLE_MAX_LENGTH} characters`);
    }

    // Simple format check - alphanumeric, spaces, and hyphens only
    if (!POSTAL_CODE_SIMPLE_REGEX.test(trimmedCode)) {
        errors.push('Postal code can only contain letters, numbers, spaces, and hyphens');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Strict postal code validation (versioned for future use)
 * Supports country-specific patterns and formats
 */
export function validatePostalCodeStrict(
    postalCode: string,
    countryCode?: string
): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];
    const trimmedCode = postalCode.trim();

    if (!trimmedCode) {
        errors.push('Postal code is required');
        return { isValid: false, errors };
    }

    if (trimmedCode.length < POSTAL_CODE_MIN_LENGTH) {
        errors.push(`Postal code must be at least ${POSTAL_CODE_MIN_LENGTH} characters`);
    }

    if (trimmedCode.length > POSTAL_CODE_MAX_LENGTH) {
        errors.push(`Postal code cannot exceed ${POSTAL_CODE_MAX_LENGTH} characters`);
    }

    // Country-specific validation
    if (countryCode && POSTAL_CODE_PATTERNS_STRICT[countryCode as keyof typeof POSTAL_CODE_PATTERNS_STRICT]) {
        const countryPattern = POSTAL_CODE_PATTERNS_STRICT[countryCode as keyof typeof POSTAL_CODE_PATTERNS_STRICT];
        if (!countryPattern.test(trimmedCode)) {
            errors.push(`Invalid postal code format for ${countryCode}`);
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

/**
 * Normalizes postal code format (simplified version)
 */
export function normalizePostalCode(postalCode: string, countryCode?: string): string {
    // For simplified validation, just trim and maintain case
    return postalCode.trim();
}

/**
 * Normalizes postal code format with country-specific logic (strict version)
 */
export function normalizePostalCodeStrict(postalCode: string, countryCode?: string): string {
    const trimmed = postalCode.trim();

    switch (countryCode) {
        case 'GB':
        case 'CA':
        case 'NL':
            return trimmed.toUpperCase();
        default:
            return trimmed;
    }
}
